// React specific
import React, { useRef } from 'react';

// Styles
import clsx from 'clsx';
import { ThemeStyleModel } from '../models/ThemeStyleModel';

// Icon
import Icon from './Icon';

// Error handling
import { useHandleError } from '../services/api';

// Button Themes
const styles: ThemeStyleModel = {
  primary:
    'w-full sm:w-120 m-0 w-140 text-base rounded border-primary-400 bg-primary-400 text-white p-13 sm:w-auto',
  grey:
    'w-100 flex-col text-xs rounded-full text-gray-400 border p-5 sm:w-auto',
  input:
    'w-full sm:w-180 m-0 w-140 text-base rounded border-primary-400 bg-primary-400 text-white p-13',
};

interface TextAnimatedButtonProps {
  firstButtonText: string;
  secondButtonText: string;
  buttonClick: () => void;
  buttonIcon?: string;
  theme: 'primary' | 'grey' | 'input';
}

const ButtonWithButtonTextAnimation = (
  textAnimatedButtonProps: TextAnimatedButtonProps
): JSX.Element => {
  const [handleError] = useHandleError();

  const {
    firstButtonText,
    secondButtonText,
    buttonIcon,
    buttonClick,
    theme,
  } = textAnimatedButtonProps;

  const firstText = useRef<HTMLParagraphElement>(null);
  const secondText = useRef<HTMLDivElement>(null);

  function handleButtonClick() {
    try {
      buttonClick();
      setTimeout(() => {
        firstText.current?.classList.add('textSlideFromCenterUp');
        secondText.current?.classList.add('textSlideFromBottomUp');
        setTimeout(() => {
          secondText.current?.classList.remove('hidden');
          firstText.current?.classList.add('hidden');
        }, 300);
      });
    } catch (error) {
      handleError(error);
    }
  }

  return (
    <button
      type="button"
      className={clsx(
        'font-pt-sans w-full items-center flex justify-center rounded-full border p-5 sm:px-30 text-xs font-bold outline-none focus:outline-none mb-10 mt-10 sm:my-0',
        {
          [styles[theme]]: theme,
        }
      )}
      onClick={handleButtonClick}
    >
      <p ref={firstText} className="font-pt-sans font-bold m-auto">
        {firstButtonText}
      </p>
      <div
        ref={secondText}
        className="flex flex-row justify-around items-center hidden w-full"
      >
        {buttonIcon ? <Icon src={buttonIcon} /> : null}
        <p className="font-pt-sans font-bold m-auto">{secondButtonText}</p>
      </div>
    </button>
  );
};

export default ButtonWithButtonTextAnimation;
